import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { Cpu, Zap, ArrowRight, Sparkles, CheckCircle2, AlertTriangle, Shield, Check } from 'lucide-react';

interface ApplianceItem {
  name: string;
  count: number;
  continuousWatts: number;
  surgeWatts: number;
}

export const AILoadEstimator: React.FC = () => {
  const store = useSolarStore();
  const [promptInput, setPromptInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    appliances: ApplianceItem[];
    totalContinuousWatts: number;
    totalSurgeWatts: number;
    recommendedPresetId: string;
    recommendedPresetName: string;
    advice: string;
  } | null>(null);
  const [applied, setApplied] = useState(false);

  const samplePrompts = [
    "2 Deep Freezers, 1 Inverter AC (1.5HP), 10 LED Bulbs, Water Pump, 65-inch Smart TV",
    "3 Bedroom Flat: 1 standing fan, 2 ceiling fans, laptop, decoder, small fridge, 8 bulbs",
    "Commercial Salon: 2 Hair Dryers, Washing Machine, 2 ACs, Sound System, Lighting",
  ];

  const handleEstimate = async (textToEstimate?: string) => {
    const text = (textToEstimate || promptInput).trim();
    if (!text || loading) return;

    setLoading(true);
    setApplied(false);

    try {
      const res = await fetch('/api/ai/estimate-load', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: text }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
      }
    } catch (err) {
      console.error("AI Load Estimator Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyToSimulator = () => {
    if (!result) return;
    store.setActiveLoad(result.totalContinuousWatts);
    store.setSelectedPreset(result.recommendedPresetId);
    store.setActiveTab('calculator');
    setApplied(true);
  };

  return (
    <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-2xl text-amber-400">
          <Cpu className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-100 flex items-center gap-2">
            AI Appliance Load & Energy Estimator
            <span className="bg-amber-500/20 text-amber-300 text-xs font-bold px-2.5 py-0.5 rounded-full border border-amber-500/30">
              GEMINI 3.6
            </span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Describe your household or office appliances in plain English. Our AI calculates continuous & surge wattage.
          </p>
        </div>
      </div>

      {/* Sample Quick Chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        {samplePrompts.map((sample, idx) => (
          <button
            key={idx}
            onClick={() => {
              setPromptInput(sample);
              handleEstimate(sample);
            }}
            className="text-xs bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-amber-300 px-3 py-1.5 rounded-xl border border-slate-700/60 transition-all flex items-center gap-1.5"
          >
            <Sparkles className="w-3 h-3 text-amber-400" />
            {sample}
          </button>
        ))}
      </div>

      {/* Input Field */}
      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <textarea
          value={promptInput}
          onChange={(e) => setPromptInput(e.target.value)}
          placeholder="E.g., 2 deep freezers, 1 inverter AC, 10 LED bulbs, water pumping machine, 65-inch TV..."
          className="flex-1 bg-slate-950/80 border border-slate-700 rounded-2xl p-4 text-xs md:text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-500 min-h-[90px] resize-none"
        />
        <button
          onClick={() => handleEstimate()}
          disabled={loading || !promptInput.trim()}
          className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 text-slate-950 font-black text-sm px-6 py-4 rounded-2xl shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2 shrink-0"
        >
          {loading ? (
            <>
              <Cpu className="w-5 h-5 animate-spin" />
              Calculating Wattage...
            </>
          ) : (
            <>
              Estimate Energy Load <Zap className="w-5 h-5" />
            </>
          )}
        </button>
      </div>

      {/* Estimation Results */}
      {result && (
        <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-6 space-y-6 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Total Continuous Power</span>
              <div className="text-2xl font-black text-amber-400 mt-1">
                {result.totalContinuousWatts.toLocaleString()} <span className="text-sm text-slate-400">Watts</span>
              </div>
            </div>
            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Motor Starting Surge Peak</span>
              <div className="text-2xl font-black text-red-400 mt-1">
                {result.totalSurgeWatts.toLocaleString()} <span className="text-sm text-slate-400">Watts Surge</span>
              </div>
            </div>
            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Recommended System</span>
              <div className="text-sm font-black text-emerald-400 mt-1">
                {result.recommendedPresetName}
              </div>
            </div>
          </div>

          {/* Breakdown Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 uppercase text-[10px] tracking-wider">
                  <th className="pb-3 font-bold">Appliance Name</th>
                  <th className="pb-3 font-bold">Quantity</th>
                  <th className="pb-3 font-bold">Continuous (W)</th>
                  <th className="pb-3 font-bold">Starting Surge (W)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-200">
                {result.appliances.map((item, i) => (
                  <tr key={i}>
                    <td className="py-2.5 font-semibold text-slate-100">{item.name}</td>
                    <td className="py-2.5 text-slate-400">{item.count}</td>
                    <td className="py-2.5 text-amber-400 font-bold">{item.continuousWatts * item.count} W</td>
                    <td className="py-2.5 text-slate-400">{item.surgeWatts * item.count} W</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* AI Advisory */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3 text-xs text-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <span className="font-bold text-amber-300">Engineering Advisory: </span>
              {result.advice}
            </div>
          </div>

          {/* Apply CTA Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
            <div className="text-xs text-slate-400">
              Click below to sync this AI estimate directly into your Access Tech ROI Solar Simulator.
            </div>
            <button
              onClick={handleApplyToSimulator}
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs px-6 py-3.5 rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
            >
              {applied ? (
                <>
                  <Check className="w-4 h-4 text-slate-950" /> Applied to Calculator Simulator!
                </>
              ) : (
                <>
                  Apply Load to Calculator Simulator <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
